import { InputLabel } from '@mui/material';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React, { memo } from 'react';
import { Controller } from 'react-hook-form';

/**
 * ControlledTextField is a wrapper around the Controller component from react-hook-form.
 * It is used to control the input fields in the form.
 * @param {object} props
 * @param {string} [props.label] - The label for the input field
 * @param {string} props.name - The name of the input field
 * @param {import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form
 * @param {string} [props.placeholder] - The placeholder for the input field
 * @param {boolean} [props.error] - The error state of the input field
 * @param {string | React.ReactNode | any} [props.helperText] - The helper text for the input field
 * @param {boolean} [props.fullWidth] - The full width state of the input field
 * @param {boolean} [props.required] - The required state of the input field
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name'>} [props.controllerOptions] - The options for the controller
 * @param {import('@mui/material/InputLabel').InputLabelProps} [props.inputLabelProps] - The props for the input label
 * @param {Omit<import('@mui/material/TextField').TextFieldProps, 'name'>} [props.textFieldProps] - The props for the text field
 * @param {import('@mui/material/Stack').StackProps} [props.stackProps] - Props for the wrapping Stack (e.g. override the default full width for inline inputs)
 */

const ControlledTextFieldComponent = ({
  name,
  control,
  label = '',
  required = false,
  placeholder = '',
  error = false,
  helperText = '',
  fullWidth = false,
  controllerOptions = {},
  inputLabelProps = {},
  textFieldProps = {},
  stackProps = {},
}) => {
  if (!name) {
    throw new Error('ControlledTextField: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledTextField: control is required');
  }

  const id = `id_${name}`;
  inputLabelProps.htmlFor = id;
  textFieldProps.id = id;

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => (
        <Stack spacing={1} width="100%" {...stackProps}>
          {label ? (
            <InputLabel htmlFor={id} required={required} shrink={false} {...inputLabelProps}>
              {label}
            </InputLabel>
          ) : null}
          <TextField
            error={error}
            fullWidth={fullWidth}
            helperText={helperText}
            id={id}
            placeholder={placeholder}
            required={required}
            size="small"
            {...field}
            {...textFieldProps}
          />
        </Stack>
      )}
    />
  );
};

ControlledTextFieldComponent.displayName = 'ControlledTextField';

const ControlledTextField = memo(ControlledTextFieldComponent);

export default ControlledTextField;
